// QueueTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { formatDistanceToNow } from 'date-fns';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Bot, Users, RefreshCw, Eye, AlertTriangle } from 'lucide-react';
import { TASK_STATUS_COLORS } from '../constants';

export const QueueTab = ({
  queueTasks = [],
  queueFilter,
  onQueueFilterChange,
  agents = [],
  onRefresh,
  onTaskClick,
  onApproveTask
}) => {
  return (
    <TabsContent value="queue" className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Select value={queueFilter.status || 'all'} onValueChange={(val) => onQueueFilterChange({...queueFilter, status: val === 'all' ? '' : val})}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={queueFilter.agent_id || 'all'} onValueChange={(val) => onQueueFilterChange({...queueFilter, agent_id: val === 'all' ? '' : val})}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map(agent => (
                <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Bot className="h-3 w-3 text-white" />
            </div>
            <span className="text-muted-foreground">AI Task</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center">
              <Users className="h-3 w-3 text-slate-600" />
            </div>
            <span className="text-muted-foreground">Human Task</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {queueTasks.map(task => (
              <div 
                key={task.id} 
                className={`p-4 border rounded-lg cursor-pointer hover:shadow-md transition-all ${
                  task.status === 'awaiting_approval' ? 'border-amber-200 bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'
                }`}
                onClick={() => onTaskClick(task)}
                data-testid={`queue-task-${task.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {task.assigned_agent_id ? (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center" title="AI Task">
                        <Bot className="h-5 w-5 text-white" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center" title="Human Task">
                        <Users className="h-5 w-5 text-slate-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {task.assigned_agent_id ? (
                        <Badge className="bg-violet-100 text-violet-700 border-violet-200 flex items-center gap-1">
                          <Bot className="h-3 w-3" /> AI Task
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 flex items-center gap-1">
                          <Users className="h-3 w-3" /> Human Task
                        </Badge>
                      )}
                      <Badge className={TASK_STATUS_COLORS[task.status]}>{task.status.replace('_', ' ')}</Badge>
                      <Badge variant="outline">{task.task_type}</Badge>
                      <Badge variant="outline" className="text-xs">P{task.priority}</Badge>
                      {task.risk_level !== 'low' && (
                        <Badge className={
                          task.risk_level === 'critical' ? 'bg-red-100 text-red-700' :
                          task.risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {task.risk_level} risk
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-semibold flex items-center gap-2">
                      {task.title}
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Queue: {task.queue_name}</span>
                      {task.assigned_agent_name && (
                        <span className="flex items-center gap-1">
                          <Bot className="h-3 w-3 text-violet-500" /> {task.assigned_agent_name}
                        </span>
                      )}
                      <span>{formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}</span>
                    </div>
                    {task.has_exceptions && (
                      <div className="mt-2 flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm">{task.exception_flags?.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  {task.status === 'awaiting_approval' && (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => onApproveTask(task.id, false)}>
                        Reject
                      </Button>
                      <Button size="sm" onClick={() => onApproveTask(task.id, true)}>
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {queueTasks.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No tasks in queue</p>
            )}
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default QueueTab;
